import { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import firebase from '../../firebase/compat'
import { client } from '../../hooks/Client'
import Location from '../../hooks/Location'
import { usePageConfig } from '../../hooks/usePageConfig'
import PageHeader from '../../components/layout/PageHeader'
import EffectDatabase from '../../components/meetstandaard/EffectDatabase'
import { useFirestoreAll } from '../../firebase/useFirestore'
import {
    activitySubtypesByType,
    activitySubtypeLabels,
    activityTypeLabels,
    deliveryModeLabels,
    estimatedEffortLabels,
} from '../../helpers/dataHelpers/activityCategories'
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined'

const REQUEST_BATCH_SIZE = 5

const getActivityMeta = (activityKey) => {
    const matchedType = Object.entries(activitySubtypesByType).find(([, subtypes]) =>
        subtypes.includes(activityKey)
    )

    if (matchedType) {
        return {
            activityType: matchedType[0],
            activitySubtype: activityKey,
            title: activitySubtypeLabels[activityKey] || activityKey,
            subtitle: activityTypeLabels[matchedType[0]] || matchedType[0],
            match: (activity) => activity.activitySubtype === activityKey,
        }
    }

    return {
        activityType: activityKey,
        activitySubtype: '',
        title: activityTypeLabels[activityKey] || activityKey,
        subtitle: '',
        match: (activity) => (activity.activityType || 'other') === activityKey,
    }
}

const getDominantValue = (values) => {
    if (!values || values.length === 0) return ''

    const counts = values.reduce((acc, value) => {
        if (!value) return acc
        acc[value] = (acc[value] || 0) + 1
        return acc
    }, {})

    return Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] || ''
}

const getLabelCounts = (items, labelMap) => {
    const counts = {}

    items.forEach((item) => {
        if (!item) return
        const label = labelMap[item] || item
        counts[label] = (counts[label] || 0) + 1
    })

    return Object.entries(counts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
}

const getAverageScore = (scores) => {
    if (!scores || scores.length === 0) return null
    return Math.round((scores.reduce((sum, value) => sum + value, 0) / scores.length) * 10) / 10
}

const BenchmarkActivityPage = () => {
    const history = useHistory()
    const { getPageConfig } = usePageConfig()
    const activityKey = Location()[4]
    const effectDatabase = EffectDatabase()

    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [effectRows, setEffectRows] = useState([])

    const activities = useFirestoreAll('Activities')
    const linkedEffects = useFirestoreAll('OutputEffects')
    const targetgroups = useFirestoreAll('Stakeholders')
    const companies = useFirestoreAll('CompagnyMeta')

    const activityMeta = getActivityMeta(activityKey)
    const filteredActivities = (activities || []).filter(activityMeta.match)

    const companySummaries = Object.values(
        filteredActivities.reduce((acc, activity) => {
            const companyId = activity.CompagnyID
            if (!companyId) return acc

            if (!acc[companyId]) {
                acc[companyId] = {
                    companyId,
                    activities: [],
                    targetgroups: new Set(),
                    deliveryModes: [],
                    efforts: [],
                }
            }

            acc[companyId].activities.push(activity)
            ;(activity.Targetgroup || []).forEach((targetgroupId) => acc[companyId].targetgroups.add(targetgroupId))
            if (activity.deliveryMode) acc[companyId].deliveryModes.push(activity.deliveryMode)
            if (activity.estimatedEffort) acc[companyId].efforts.push(activity.estimatedEffort)

            return acc
        }, {})
    ).map((summary) => ({
        companyId: summary.companyId,
        activityCount: summary.activities.length,
        dominantEffort: getDominantValue(summary.efforts),
        dominantDeliveryMode: getDominantValue(summary.deliveryModes),
        targetgroupIds: [...summary.targetgroups],
    }))

    const companyIdSet = new Set(companySummaries.map((summary) => summary.companyId))
    const companyIdsKey = [...companyIdSet].sort().join('|')
    const selectedActivityIds = new Set(filteredActivities.map((activity) => activity.ID))
    const benchmarkEffects = Object.values(
        (linkedEffects || []).reduce((acc, effect) => {
            if (!effect.MSIId) return acc
            const linkedActivityIds = Array.isArray(effect.Activity) ? effect.Activity : []
            if (!linkedActivityIds.some((activityId) => selectedActivityIds.has(activityId))) return acc
            if (!companyIdSet.has(effect.CompagnyID)) return acc

            const key = effect.MSIId
            if (!acc[key]) {
                acc[key] = {
                    effectId: effect.MSIId,
                    effectName: effect.Effect || effect.MSIId,
                    categoryName: effect.MainEffect || '',
                    linkedCompanyIds: new Set(),
                }
            }

            acc[key].linkedCompanyIds.add(effect.CompagnyID)
            if (effect.Effect) acc[key].effectName = effect.Effect
            if (effect.MainEffect) acc[key].categoryName = effect.MainEffect

            return acc
        }, {})
    ).map((effect) => ({
        ...effect,
        linkedCompanyIds: [...effect.linkedCompanyIds],
    }))
    const benchmarkEffectsKey = benchmarkEffects
        .map((effect) => `${effect.effectId}:${effect.linkedCompanyIds.sort().join(',')}`)
        .sort()
        .join('|')
    const companyNameMap = (companies || []).reduce((acc, item) => {
        acc[item.CompagnyID] = item.CommunityName || item.Compagny || item.CompagnyID
        return acc
    }, {})
    const targetgroupMap = (targetgroups || []).reduce((acc, item) => {
        acc[item.ID] = item.Organisation || item.Title || item.ID
        return acc
    }, {})

    useEffect(() => {
        if (benchmarkEffects.length === 0 || companyIdSet.size === 0) {
            setEffectRows([])
            return
        }

        let cancelled = false

        const loadBenchmarks = async () => {
            setLoading(true)
            setError('')
            setEffectRows([])

            const msiBenchmark = firebase.app().functions('europe-west1').httpsCallable('msiBenchmark')
            const nextRows = []

            for (let i = 0; i < benchmarkEffects.length; i += REQUEST_BATCH_SIZE) {
                const chunk = benchmarkEffects.slice(i, i + REQUEST_BATCH_SIZE)
                const chunkResults = await Promise.all(
                    chunk.map(async (effect) => {
                        const response = await msiBenchmark({ msiId: effect.effectId })
                        const result = response.data || {}
                        const companyScores = (result.respondentsPerCompany || [])
                            .filter((company) =>
                                effect.linkedCompanyIds.includes(company.companyId) &&
                                company.benchmarkGrowth != null
                            )
                            .sort((a, b) => b.benchmarkGrowth - a.benchmarkGrowth)

                        if (companyScores.length === 0) return null

                        const scores = companyScores.map((company) => company.benchmarkGrowth)
                        const average = getAverageScore(scores)

                        return {
                            ...effect,
                            average,
                            min: Math.min(...scores),
                            max: Math.max(...scores),
                            companyCount: companyScores.length,
                            companyScores: companyScores.map((company, index) => ({
                                ...company,
                                rank: index + 1,
                                delta: average == null
                                    ? null
                                    : Math.round((company.benchmarkGrowth - average) * 10) / 10,
                            })),
                        }
                    })
                )

                nextRows.push(...chunkResults.filter(Boolean))

                if (!cancelled) {
                    setEffectRows(
                        [...nextRows].sort((a, b) => {
                            if (b.companyCount !== a.companyCount) return b.companyCount - a.companyCount
                            return (b.average ?? -Infinity) - (a.average ?? -Infinity)
                        })
                    )
                }
            }

            if (!cancelled) setLoading(false)
        }

        loadBenchmarks().catch((err) => {
            console.error('BenchmarkActivityPage error:', err)
            if (!cancelled) {
                setError('Benchmark activiteitspagina kon niet worden geladen.')
                setLoading(false)
            }
        })

        return () => {
            cancelled = true
        }
    }, [activityKey, benchmarkEffectsKey, companyIdsKey])

    const matrixCompanies = [...companySummaries]
        .map((summary) => {
            const scores = effectRows
                .map((effect) => effect.companyScores.find((item) => item.companyId === summary.companyId)?.benchmarkGrowth)
                .filter((value) => value != null)

            return {
                ...summary,
                averageScore: getAverageScore(scores),
                companyName: companyNameMap[summary.companyId] || summary.companyId,
            }
        })
        .sort((a, b) => {
            if ((b.averageScore ?? -Infinity) !== (a.averageScore ?? -Infinity)) {
                return (b.averageScore ?? -Infinity) - (a.averageScore ?? -Infinity)
            }

            return a.companyName.localeCompare(b.companyName)
        })

    const leadEffect = effectRows[0] || null
    const topPerformers = leadEffect ? leadEffect.companyScores.slice(0, 3) : []
    const lowPerformers = leadEffect ? [...leadEffect.companyScores].slice(-3).reverse() : []

    const getCompanyProfiles = (companyIds) =>
        matrixCompanies.filter((company) => companyIds.includes(company.companyId))

    const topProfiles = getCompanyProfiles(topPerformers.map((item) => item.companyId))
    const lowProfiles = getCompanyProfiles(lowPerformers.map((item) => item.companyId))

    const topIntensity = getLabelCounts(topProfiles.map((item) => item.dominantEffort), estimatedEffortLabels)[0]?.[0] || ''
    const topDeliveryMode = getLabelCounts(topProfiles.map((item) => item.dominantDeliveryMode), deliveryModeLabels)[0]?.[0] || ''
    const topTargetgroup = getLabelCounts(
        topProfiles.flatMap((item) => item.targetgroupIds).map((id) => targetgroupMap[id]),
        {}
    )[0]?.[0] || ''
    const lowIntensity = getLabelCounts(lowProfiles.map((item) => item.dominantEffort), estimatedEffortLabels)[0]?.[0] || ''
    const lowDeliveryMode = getLabelCounts(lowProfiles.map((item) => item.dominantDeliveryMode), deliveryModeLabels)[0]?.[0] || ''
    const lowTargetgroup = getLabelCounts(
        lowProfiles.flatMap((item) => item.targetgroupIds).map((id) => targetgroupMap[id]),
        {}
    )[0]?.[0] || ''

    const insightParts = [topDeliveryMode, topIntensity, topTargetgroup].filter(Boolean)
    const firstInsight = leadEffect && insightParts.length > 0
        ? `Binnen ${activityMeta.title.toLowerCase()} lijken organisaties met ${insightParts.join(', ')} beter te scoren op ${leadEffect.effectName}.`
        : ''
    const benchmarkActivityPageConfig = getPageConfig('benchmarkactivity')

    return (
        <div className='main'>
            <div className='main-container'>
                <div className='benchmark-details-container'>
                    <div
                        className='bcd-back'
                        onClick={() => history.goBack()}
                    >
                        <ArrowBackOutlinedIcon style={{ fontSize: 16 }} />
                        Terug naar {benchmarkActivityPageConfig.backLabel || 'benchmark details'}
                    </div>

                    <PageHeader
                        heading={activityMeta.title}
                        subHeading={activityMeta.subtitle || 'Benchmark activiteit'}
                        description='Analyse van organisaties die deze activiteit uitvoeren, hun benchmarkscores per effect en de profielverschillen tussen sterke en zwakke performers.'
                        AI={false}
                    />

                    <div className='bap-layout'>
                        <div className='bap-block'>
                            <p className='bap-label'>Organisaties die deze activiteit doen</p>
                            <div className='bap-company-grid'>
                                {matrixCompanies.map((company) => (
                                    <div key={company.companyId} className='bap-company-card'>
                                        <p className='bap-company-name'>{company.companyName}</p>
                                        <div className='bap-company-meta'>
                                            <span>{company.activityCount} activiteit{company.activityCount !== 1 ? 'en' : ''}</span>
                                            <span>{estimatedEffortLabels[company.dominantEffort]?.split(' (')[0] || 'Geen intensiteit'}</span>
                                            <span>{deliveryModeLabels[company.dominantDeliveryMode] || 'Geen vorm'}</span>
                                        </div>
                                    </div>
                                ))}
                                {!loading && matrixCompanies.length === 0 && (
                                    <p className='bcd-empty'>Geen organisaties gevonden voor deze activiteit.</p>
                                )}
                            </div>
                        </div>

                        <div className='bap-block'>
                            <p className='bap-label'>Benchmark scores per effect</p>
                            {error && <p className='bcd-error'>{error}</p>}
                            {loading && <p className='bcd-loading'>Benchmarks worden geladen…</p>}
                            {!loading && !error && effectRows.length > 0 && (
                                <div className='bap-effect-list'>
                                    {effectRows.map((effect) => (
                                        <div key={effect.effectId} className='bap-effect-row'>
                                            <div className='bap-effect-header'>
                                                <div>
                                                    <p className='bap-effect-name'>{effect.effectName}</p>
                                                    <p className='bap-effect-category'>{effect.categoryName}</p>
                                                </div>
                                                <div className='bap-effect-stats'>
                                                    <span>Gem. {effect.average}%</span>
                                                    <span>Min {effect.min}%</span>
                                                    <span>Max {effect.max}%</span>
                                                    <span>{effect.companyCount} org.</span>
                                                </div>
                                            </div>
                                            <div className='bap-ranking-list'>
                                                {effect.companyScores.slice(0, 5).map((company) => (
                                                    <div key={company.companyId} className='bap-ranking-row'>
                                                        <span className='bap-ranking-rank'>#{company.rank}</span>
                                                        <span className='bap-ranking-name'>
                                                            {companyNameMap[company.companyId] || company.companyId}
                                                        </span>
                                                        <span className='bap-ranking-score'>{company.benchmarkGrowth}%</span>
                                                        <span className={`bap-ranking-delta${company.delta > 0 ? ' bap-ranking-delta--positive' : company.delta < 0 ? ' bap-ranking-delta--negative' : ''}`}>
                                                            {company.delta == null ? '—' : `${company.delta > 0 ? '+' : ''}${company.delta}%`}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className='bap-block bap-block--wide'>
                            <p className='bap-label'>Vergelijkingsmatrix</p>
                            {!loading && !error && effectRows.length > 0 && (
                                <div className='bap-matrix-wrap'>
                                    <table className='bap-matrix-table'>
                                        <thead>
                                            <tr>
                                                <th>Organisatie</th>
                                                {effectRows.map((effect) => (
                                                    <th key={effect.effectId}>{effect.effectName}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {matrixCompanies.map((company) => (
                                                <tr key={company.companyId}>
                                                    <td>{company.companyName}</td>
                                                    {effectRows.map((effect) => {
                                                        const score = effect.companyScores.find((item) => item.companyId === company.companyId)
                                                        return (
                                                            <td key={`${company.companyId}-${effect.effectId}`}>
                                                                {score ? (
                                                                    <div className='bap-matrix-score'>
                                                                        <span>{score.benchmarkGrowth}%</span>
                                                                        <small>{score.delta > 0 ? '+' : ''}{score.delta}%</small>
                                                                    </div>
                                                                ) : '—'}
                                                            </td>
                                                        )
                                                    })}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        <div className='bap-block'>
                            <p className='bap-label'>Vergelijking / rangorde</p>
                            {leadEffect ? (
                                <>
                                    <p className='bap-section-title'>Sterkste vergelijking op {leadEffect.effectName}</p>
                                    <div className='bap-rank-columns'>
                                        <div>
                                            <p className='bap-rank-label'>Top performers</p>
                                            {topPerformers.map((company) => (
                                                <div key={company.companyId} className='bap-rank-item'>
                                                    <span>{companyNameMap[company.companyId] || company.companyId}</span>
                                                    <strong>{company.benchmarkGrowth}%</strong>
                                                </div>
                                            ))}
                                        </div>
                                        <div>
                                            <p className='bap-rank-label'>Lage performers</p>
                                            {lowPerformers.map((company) => (
                                                <div key={company.companyId} className='bap-rank-item'>
                                                    <span>{companyNameMap[company.companyId] || company.companyId}</span>
                                                    <strong>{company.benchmarkGrowth}%</strong>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <p className='bcd-empty'>Nog geen benchmarkvergelijking beschikbaar.</p>
                            )}
                        </div>

                        <div className='bap-block'>
                            <p className='bap-label'>Profielverschillen</p>
                            {leadEffect ? (
                                <div className='bap-profile-diff'>
                                    <p>
                                        Top performers werken vaker met <strong>{topDeliveryMode || 'onbekende vorm'}</strong> en hebben vaker een
                                        <strong> {topIntensity || 'onbekend'} </strong>intensiteit.
                                    </p>
                                    <p>
                                        Meest zichtbare doelgroep bij top performers: <strong>{topTargetgroup || 'onbekend'}</strong>.
                                    </p>
                                    <p>
                                        Lagere performers laten vaker <strong>{lowDeliveryMode || 'onbekende vorm'}</strong>,
                                        <strong> {lowIntensity || 'onbekende'} </strong>intensiteit en doelgroep
                                        <strong> {lowTargetgroup || 'onbekend'}</strong> zien.
                                    </p>
                                    <p>
                                        In deze vergelijking zijn {topPerformers.length} top performers en {lowPerformers.length} lage performers meegenomen op
                                        <strong> {leadEffect.effectName}</strong>.
                                    </p>
                                </div>
                            ) : (
                                <p className='bcd-empty'>Onvoldoende data voor profielvergelijking.</p>
                            )}
                        </div>

                        <div className='bap-block bap-block--wide'>
                            <p className='bap-label'>Eerste insight</p>
                            {firstInsight ? <p className='bap-insight'>{firstInsight}</p> : <p className='bcd-empty'>Nog geen duidelijke eerste insight.</p>}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default BenchmarkActivityPage
